/**
 * Every tunable number in the game lives here so that balancing is a single-file
 * job (task M6-4) and so the values can be asserted in tests.
 */
export const BALANCE = {
  simulation: {
    /** Fixed simulation step in seconds. */
    step: 1 / 60,
    /** Maximum simulation steps executed per rendered frame. */
    maxStepsPerFrame: 5,
    /** Longest frame delta that will be simulated, in seconds. */
    maxFrameDelta: 0.25,
  },

  player: {
    /** Distance from the body centre to the contact point (feet) along the surface normal. */
    attachHeight: 0.45,
    /** How far the magnet may give, metres. A landing compresses this much. */
    attachCompliance: 0.12,
    /** The spring that holds the body on the surface. */
    attachStiffness: 220,
    /** The damper that settles it. */
    attachDamping: 22,
    /** How much the body banks into a turn, radians per radian per second. */
    leanFactor: 0.05,
    /** The most the body may bank, radians. */
    maxLean: 0.16,
    /** Radius used for the coarse body sphere when resolving collisions. */
    bodyRadius: 0.45,
    /** How close the body centre may get to a surface while free-floating.
     *  This has to stay below the attach height so the feet can reach a
     *  surface and trigger a landing. */
    wallClearance: 0.12,
    walkSpeed: 6,
    walkAcceleration: 18,
    walkBraking: 24,
    /** Walk speed multiplier while sneaking, 0 to 1. */
    sneakSpeedScale: 0.38,
    /** How far the body drops toward the surface while sneaking, metres. */
    sneakCrouchDrop: 0.16,
    /** How quickly the body settles into and out of the sneak pose, per second. */
    sneakBlendRate: 10,
    /** Maximum angular rate of the body while it turns toward its velocity, rad/s. */
    turnRate: 6,
    /** Thrust acceleration while free-floating, m/s^2. */
    thrustAcceleration: 6,
    /** Linear damping applied to free-flight velocity, per second. */
    flightDamping: 0.15,
    /** Impulse applied by a normal jump, m/s. */
    jumpImpulse: 5.2,
    /** Extra impulse multiplier when the launch is charged with crouch. */
    chargeMultiplier: 1.45,
    /** Minimum launch component along the surface normal, so a jump aimed
     *  along the floor still leaves the surface instead of skidding. */
    jumpMinNormal: 0.25,
    /** How much walking momentum a launch keeps; the rest is aimed. */
    jumpMomentumRetention: 0.35,
    /** Time to charge a full launch, seconds. */
    chargeTime: 0.8,
    /** Time after leaving a surface during which the body may still jump (coyote time). */
    coyoteTime: 0.12,
    /** Time a jump press is remembered while airborne or landing, seconds. */
    jumpBufferTime: 0.18,
    /** Distance at which a magnetic surface can be grabbed while free-floating. */
    grabRadius: 0.6,
    /**
     * How far short of a doorway opening the magnet stops the body, metres.
     * A small inset keeps the support point clear of the rim instead of
     * resting exactly on the edge, where float noise could flicker it.
     */
    holeEdgeMargin: 0.05,
    /** Distance walked between footstep sounds, metres. */
    stepStride: 1.15,
    /** How far ahead the body looks for the surface it is heading for, metres. */
    landingLookAhead: 45,
    /** The shortest time the mid-air reorientation is allowed to take, seconds. */
    minFlightRotationTime: 0.35,
    /** Maximum player health. */
    maxHealth: 100,
  },

  transition: {
    /** Duration of the surface change blend, seconds. */
    duration: 0.42,
    /** Minimum time between two surface transitions, seconds. */
    cooldown: 0.08,
    /** How far before the edge the corner blend starts, metres. */
    band: 0.4,
    /** How fast the body must be moving to enter the band, metres per second. */
    minSpeed: 2.5,
    /**
     * The least movement input that counts as a step across a seam.
     *
     * The rule is the input, not the view: any held direction (W/S, A/D, or a
     * diagonal) crosses, so a wall or ceiling can be walked onto head-on. This
     * is the floor under the larger of `|x|` and `|y|`, so stick noise cannot
     * cross on its own and a body coasting with no input never climbs.
     */
    stepMin: 0.15,
  },

  landing: {
    /** Duration of the landing blend, seconds. A ceiling landing flips the
     *  body 180 degrees, so this needs to be slower than a corner transition. */
    duration: 0.48,
    /** How close the body has to be to a surface for the magnetic feet to grab. */
    contactDistance: 0.3,
    /** Velocity damping factor applied on contact. */
    damping: 0.18,
    /** Maximum time spent in the landing state before returning to free flight. */
    timeout: 0.65,
    /** Impact speed above which the landing shake is at maximum. */
    maxImpactSpeed: 14,
  },

  camera: {
    thirdPerson: {
      distance: 4.5,
      height: 1.6,
      /** How quickly the camera catches up with its target, per second. */
      smoothing: 9,
      /** Vertical orbit limits for the camera arm, degrees. The view itself
       *  follows the head pitch, so these only keep the arm out of the floor
       *  and the sky. */
      minPitch: -60,
      maxPitch: 35,
      /** Camera collision sphere radius. */
      collisionRadius: 0.35,
    },
    /** Off-axis projection: the first-person camera becomes a window into the
     *  world, driven by the head-tracking viewer offset. */
    offAxis: {
      /**
       * Distance from the viewer to the virtual screen, metres.
       *
       * Arm's length, which is where a laptop webcam sits. The window's angular
       * response is the viewer offset over this distance, so at 1.2 m a 30 cm
       * lean read as ~14 degrees instead of the ~27 a real window at 60 cm
       * gives: the depth was there but the movement was half speed.
       */
      screenDistance: 0.6,
      /** The closest the viewer may get to the screen. */
      minScreenDistance: 0.25,
      /** The most the viewer may move sideways/up/forward, metres. Reaches are
       *  smaller at arm's length than across a desk, and the clamp is what stops
       *  a tracker spike from skewing the window. */
      maxViewerX: 0.45,
      maxViewerY: 0.3,
      maxViewerForward: 0.3,
      /** The multiplier applied when reduced motion is on. */
      reducedMotionScale: 0.35,
      /**
       * Default extra multiplier on the viewer offset in third person.
       *
       * The camera translation *and* the matching frustum shift are scaled by
       * the same value, so the window stays anchored and the robot and the
       * walls slide against each other instead of the picture panning as one
       * piece. It was 2, which read as too much from behind the body: the far
       * field slid half the screen width on a full lean. At 1 the third-person
       * window matches the calibrated first-person one. The player can turn the
       * arm slide back up with the third-person parallax slider, which
       * multiplies this.
       */
      thirdPersonParallaxScale: 1,
    },
    firstPerson: {
      /** Near clip plane: small enough that the head can get close to a wall. */
      near: 0.05,
      minPitch: -90,
      maxPitch: 90,
      /** Offset of the eye point from the head pivot, at the camera lens. */
      eyeOffset: [0, 0.12, -0.14] as [number, number, number],
      /** Distance from the head at which the camera is pulled in. */
      pullInDistance: 0.25,
    },
    /** Duration of the mode blend, seconds. */
    blendDuration: 0.2,
    /** Sensitivity of the mouse look, radians per pixel. */
    lookSensitivity: 0.0022,
    defaultFov: 72,
    /** The narrowest and widest the field of view may go, degrees. */
    minFov: 50,
    maxFov: 120,
    /** How fast the camera's up follows the body while it turns in mid air. */
    upDamping: 7,
    /**
     * The camera's plane-swivel model: a sideways step leaves the picture on
     * the plane the player stepped off, and the view swings onto the new
     * surface only when the player asks (a head tilt, or `Q`).
     *
     * `duration` is the swing once it starts; `tiltThreshold` and
     * `tiltHoldTime` are the head-tilt gesture: a roll past the angle, held
     * for the time, is a deliberate "settle me onto this wall".
     */
    swivel: {
      duration: 0.9,
      /** Head roll that counts as a deliberate tilt, radians. */
      tiltThreshold: 0.5,
      /** How long the tilt must be held, seconds. */
      tiltHoldTime: 0.15,
    },
    /**
     * How fast the camera's up follows the body during a corner or landing.
     *
     * Slow on purpose. The aim and the steering follow the body's new frame
     * immediately, so the roll is the only thing left carrying the surface
     * change: easing it in over about half a second lets the new floor arrive
     * instead of spinning the player onto it.
     */
    transitionUpDamping: 2.5,
    /**
     * How fast it catches up once the body is back on a surface.
     *
     * Also slower than a snap, so the tail of a long reorientation finishes
     * smoothly rather than jumping the last few degrees.
     */
    surfaceUpDamping: 8,
    /** Fraction of the FOV kick used while a corner or landing is blending. */
    transitionFovKickScale: 0.3,
    /** How much of the body's roll the first person view keeps, 0 to 1. */
    rollDamping: 0.45,
    /** How much the field of view opens per radian of body rotation, degrees. */
    fovKickPerRadian: 3.5,
    /** The most the field of view may open up while the body turns, degrees. */
    maxFovKick: 5.5,
    head: {
      /** The turret can face the full circle; this is only a sanity clamp. */
      maxYaw: 360,
      /** The turret can look straight up or down, degrees. */
      maxPitch: 90,
    },
  },

  combat: {
    fireInterval: 0.16,
    damage: 25,
    range: 120,
    recoil: 0.012,
    /** View kick per unit of weapon recoil. */
    recoilViewScale: 2.5,
    /** Maximum view kick from recoil, radians. */
    maxRecoilViewKick: 0.07,
    /** Close-range claw strike (F): reach, damage and recharge, seconds.
     *  The claw kills a drone outright, netted or not. */
    meleeRange: 1.8,
    meleeDamage: 50,
    meleeCooldown: 0.55,
    /** How long the claw strike animation plays, seconds. Under the cooldown. */
    meleeStrikeDuration: 0.45,
    /** How hard the strike shoves the body toward the target, m/s. */
    meleeLunge: 3.5,
    /** Drone health. */
    droneHealth: 50,
    /** Score awarded per drone destroyed. */
    scorePerDrone: 100,
    /** Damage a drone bolt deals. A burst still is not lethal to a full hull,
     *  but standing in the open now costs far more than a scratch. */
    boltDamage: 8,
    // Rifle ricochet: a shot that clips a surface at a shallow enough angle can
    // skip off it, up to `ricochetMaxBounces` times. See `combat/Ricochet.ts`.
    /** Most surfaces a single rifle shot can bounce off before it stops. */
    ricochetMaxBounces: 2,
    /**
     * The grazing angle above which a shot never bounces, radians. `~0.5` is
     * about 29 degrees off the surface: steeper than that and the bullet bites
     * instead of skipping.
     */
    ricochetCriticalAngle: 0.5,
    /** Chance of a bounce at a dead-parallel graze; ramps down to the critical angle. */
    ricochetMaxChance: 0.85,
    /**
     * Chance a *bounced* shot that lands on exterior hull breaches the ship.
     * A direct hull hit always breaches; this is the softer, riskier path.
     */
    ricochetBreachChance: 0.35,
    /** Speed of drone projectiles, m/s. */
    boltSpeed: 16,
    /** Lifetime of a drone projectile, seconds. */
    boltLifetime: 4,
    /**
     * The pop-out.
     *
     * During the focus window *every* bolt is drawn through the overlay at its
     * true transform, so all bolts ride the frame as one continuous
     * representation (the world bolt layer is hidden). A bolt crossing the bars
     * is drawn over them, then clips out of the viewport while its trail sweeps
     * past behind it. `boltPopBehindDistance` is how far its own length behind
     * the eye a bolt may travel before it stops being drawn (it has gone by).
     */
    boltPopBehindDistance: 1.2,
    /**
     * Speed a bolt hit adds to the body, m/s. On a surface the walk braking
     * turns it into a shove; in free flight it carries, so a hit floats you off.
     */
    boltKnockback: 2.6,
    // The burst cadence, wake-up and aim lead all vary by the level's
    // difficulty tier: see `config/difficulty.ts` (that is where
    // `droneBurstCount` and friends moved to).
    /** Distance within which a drone will engage the player. */
    droneEngageRange: 16,
    /** Grace period before the drones open fire at the start of a round, seconds.
     *  Without it the player is hit before they can see the first drone. */
    droneEngageDelay: 1.6,
    /** Height a drone hovers above its anchor surface, metres. */
    droneHoverHeight: 0.9,
    /** Patrol orbit radius range around the anchor, metres. */
    dronePatrolRadiusMin: 0.9,
    dronePatrolRadiusMax: 2.2,
    /** Patrol angular speed range, radians per second. */
    dronePatrolSpeedMin: 0.55,
    dronePatrolSpeedMax: 0.9,
    /** Speed a drone flies along an authored waypoint route, metres per second.
     *  Slower than the player walks, so a route reads as a sweep rather than a
     *  chase. */
    droneRouteSpeed: 1.6,
    /** Hover bob speed range, radians per second. */
    droneBobSpeedMin: 1.6,
    droneBobSpeedMax: 2.8,
    /** Hover bob amplitude range, metres. */
    droneBobAmountMin: 0.25,
    droneBobAmountMax: 0.45,
    /** Constant drift offset range, metres. */
    droneDrift: 0.12,
    /** Margin kept between a patrol orbit and the surface edge, metres. */
    droneEdgeMargin: 0.9,
  },

  bulletTime: {
    /** World and control time scale while a dodge is active. */
    scale: 0.2,
    /** Shortest a dodge window lasts, seconds. */
    duration: 1.5,
    /**
     * Longest one dodge window lasts, however many bolts are still in the air,
     * seconds. A burst that is still incoming when a window ends opens the next
     * one on the same frame, so this bounds a single window rather than the
     * total time the world can stay slowed.
     */
    maxDuration: 5,
    /**
     * A bolt still on its way that will pass within this of the body holds the
     * window open, metres. It is wider than `threatRadius` because the point of
     * the dodge is that the burst then misses: a bolt flying past a metre away
     * still reads as "still coming".
     */
    holdRadius: 1.25,
    /** Grace after the last bolt goes by, seconds. */
    holdMargin: 0.15,
    /** A bolt is a threat if it can reach the player within this time, seconds. */
    threatWindow: 0.6,
    /** A bolt must pass within this distance of the body to be a threat, metres. */
    threatRadius: 0.75,
    /** How strongly the assist pushes the body, metres per second. */
    assistSpeed: 3,
    /**
     * The near-miss assist follows the single strongest threat, and these tune
     * how that choice and the push behave.
     *
     * `assistSmoothing` is the exponential rate the published assist eases at
     * (per second), so a change of direction reads as a lean rather than a snap.
     * `assistRelease` is how fast the claim on a direction fades once its bolt
     * is gone (weight per second), and `assistHysteresis` is the weight margin a
     * threat on the opposite side must beat before it takes the direction over,
     * so two similar bolts cannot make the dodge flip side to side.
     */
    assistSmoothing: 10,
    assistRelease: 3,
    assistHysteresis: 0.25,
    /** Head-shift dead zone during bullet time, metres. */
    shiftDeadZone: 0.05,
    /**
     * How far a full head lean swivels the body during bullet time, metres.
     *
     * This is only the shipped default: the player tunes it live with the
     * `dodgeSwivelRange` setting (the "Dodge swivel range" slider), so it is
     * kept here only as the documented baseline. The lean is a bounded offset,
     * not a strafe: the body swings out along the lean and comes home when the
     * window closes or the head centres, so a dodge can never strand the player
     * away from their path. Bullet time magnifies the range rather than the
     * request, which keeps the swing proportional to the lean.
     */
    swayRange: 1,
    /** How far a full vertical head shift can move the body, metres. */
    shiftVerticalRange: 0.3,
    /** How quickly the vertical body dodge settles, per second. */
    shiftVerticalDamping: 12,
  },

  /** How far noises carry and how quickly a drone reacts to hearing one. */
  stealth: {
    /** Radius in which a walking footstep wakes a drone, metres. */
    footstepNoiseRadius: 9,
    /** Radius in which a landing wakes a drone, metres. */
    landingNoiseRadius: 12,
    /** Radius in which the AR or net wakes a drone, metres. */
    shotNoiseRadius: 20,
    /** Reaction time after a drone hears a noise, seconds. */
    hearingReaction: 0.2,
    /** How long a drone stays suspicious after hearing a noise, seconds. */
    hearingMemory: 2.5,
  },

  tools: {
    /** Magnetic grapple hook. */
    grappleRange: 12,
    grappleSpeed: 30,
    /** Constant reel speed toward the anchor, metres per second. */
    grapplePullSpeed: 12,
    grappleCooldown: 1.5,
    /**
     * Distance from the body to the anchor at which a wall reel lets go,
     * metres. The hook releases just short of the surface so the magnet can
     * catch the body and play its landing, instead of dragging it through the
     * floor and back off again.
     */
    grappleReleaseDistance: 0.6,
    /** Short-range capture net. */
    netRange: 6,
    netDuration: 4,
    netCooldown: 6,
    /** Half-angle of the net cone, radians. */
    netCone: 0.6,
  },

  headTracking: {
    /** Dead zone around the calibrated neutral viewer position, metres. */
    viewDeadZone: 0.01,
    /** Safety clamp on the viewer position, metres. */
    viewLimitX: 1.2,
    viewLimitY: 0.9,
    viewLimitZ: 0.8,
    /**
     * Normalised iris offset to metres, used by the landmark extraction.
     *
     * The vertical channel is deliberately half the lateral one: the irises
     * travel a long way when the player nods, and that reads as the face
     * "moving" the window far more than the actual head translation does.
     * Leaning sideways and forward/back are clean translation and stay at full
     * strength.
     */
    viewScaleX: 3.6,
    viewScaleY: 1.2,
    /** Iris separation -> viewer distance, metres. */
    viewDistance: 0.06,
    /**
     * Head movement scaling for screen calibration.
     *
     * When the player has calibrated their screen, the normalized head position
     * is mapped to real metres: a head that crosses the whole camera frame
     * spans about this many screen widths. It is the off-axis viewer's own
     * heuristic (V4C38/sensai-off-axis) and only applies once a calibration is
     * stored; without one the fixed `viewScaleX/Y` above are used unchanged.
     */
    screenMovementScale: 1.5,
    /**
     * Calibrated reach, as a fraction of the full-camera-frame offset.
     *
     * Once the screen is calibrated the viewer offset is in real metres, far
     * smaller than the fixed `camera.offAxis.maxViewer*` clamp the uncalibrated
     * path uses. These fractions rebuild that reach from the calibration so the
     * window clamp, the body-dodge normalisation and the head-map dead zone all
     * stay the same *fraction of a lean*, instead of going limp. A full lean
     * reaches this much of the head's full-frame travel.
     */
    screenReachFraction: 0.85,
    /** Forward/back reach, as a fraction of the lateral one. */
    screenReachForwardFraction: 0.6,
    /** Centre dead zone for the body sway, as a fraction of the lateral reach. */
    screenReachDeadFraction: 0.15,
    /**
     * Webcam reference for auto-estimating viewing distance, cm at a
     * normalized iris separation of `distanceReferenceSeparation`. A typical
     * laptop webcam at 60 cm gives about 0.075, but the exact value depends on
     * the camera's field of view, so the manual field always wins.
     */
    distanceReferenceCm: 60,
    distanceReferenceSeparation: 0.075,
    /**
     * Centre dead zone for the body sway, in metres of viewer offset. Inside
     * this band a centred head pose produces no sway, so aiming is stable;
     * past it the sway ramps up from zero.
     *
     * It is a fifth of the lateral reach, so it scales with it: when the window
     * moved to arm's length the reach fell from 1.2 m to 0.45 m, and a fixed
     * 0.2 m band would have eaten half of it (and two thirds of the vertical
     * reach). Tracker jitter is centimetres, so the band is still comfortably
     * above the noise.
     */
    bodyShiftDeadZone: 0.075,
    /**
     * How far a full head lean swivels the body, metres.
     *
     * The lean is a bounded offset rather than a strafe: it swings the body
     * out along the lean and back home when the lean centres, so it can never
     * carry the player away from the path they were walking. Bullet time raises
     * the range (see `bulletTime.swayRange`).
     */
    swayRange: 0.45,
    /** How quickly the body swings to the lean, per second. */
    swayDamping: 10,
    /** A smaller forward/back component of the same lean, 0 to 1. */
    bodyShiftForwardScale: 0.15,
    /** How quickly the measured head roll follows the face, per second. */
    rollDamping: 12,
    /**
     * Legacy One Euro filter parameters, used only by `?leanlegacy`. The live
     * pipeline runs the Kalman below, which smooths the whole signal at all
     * times (the old design smoothed only during bullet time).
     */
    minCutoff: 2.5,
    beta: 0.04,
    /**
     * The constant-velocity Kalman that smooths every axis of the head pose.
     *
     * `kalmanProcessNoise` is the acceleration-noise density: higher tracks a
     * fast lean with less lag. `kalmanMeasurementNoise` is the sample variance:
     * higher trusts the model and produces a stiller window. `kalmanGate` is
     * how many standard deviations an innovation may reach before it is
     * clamped, which swallows a blink or a tracking glitch instead of letting
     * it yank the view. Tuned for roughly 100 ms of lag on a full lean with the
     * jitter gone.
     */
    kalmanProcessNoise: 10,
    kalmanMeasurementNoise: 0.0006,
    kalmanGate: 3,
    /**
     * Multiplier on the lean's viewer offset. 1 is the calibrated response;
     * raise it to punch up the dodge if the physical scale feels weak.
     */
    leanFeelBoost: 1,
    /**
     * Most the body tilts into a dodge, radians. The rig leans into the sway
     * (roll for a lateral lean, a backward bend for a forward one), so the
     * swivel reads on the robot itself in third person.
     */
    dodgeTilt: 0.18,
    /** Maximum inference rate, Hz. */
    maxRate: 60,
  },

  touch: {
    /** Renderer pixel ratio ceiling on phones and tablets. */
    pixelRatio: 1.5,
    /**
     * Head-tracking inference rate ceiling, Hz.
     *
     * MediaPipe on a phone's GPU has a much tighter budget than on a laptop,
     * and a lean dodge does not need 60 Hz to read. The window still updates
     * every frame; only new inferences are throttled.
     */
    headTrackingRate: 30,
    /**
     * Base look pixels produced by one pixel of drag.
     *
     * A thumb drag is shorter than a mouse swipe, so the same sensitivity
     * would feel sluggish; this base multiplier restores the reach. The
     * player's **Touch look sensitivity** setting scales it further, because
     * the right rate depends on the device and the hand holding it.
     */
    lookScale: 1.4,
  },

  audio: {
    master: 0.7,
    sfx: 0.9,
    ambience: 0.35,
    music: 0.3,
  },
} as const;

export type Balance = typeof BALANCE;
